import React, {useMemo} from 'react';
import {Alert, Box, Grid, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {ORANGE_COLOR} from '../../../styles';
import {AcwrScore} from '../../../interfaces';
import PlayerACWRGasGauge from './PlayerACWRGasGauge';
import {getPreviousISODates} from '../../../helpers';
import {getMostRecentAcwrScore} from '../../../services/acwrService';
import {ACWR_MAX} from '../../../constants';
import LoadGraph from '../../shared/LoadGraph';

interface Props {
  acwrScores: AcwrScore[];
}

const PlayerLoadRow: React.FC<Props> = ({acwrScores}) => {
  const {t} = useTranslation();

  // only show acwr if its from the last 2 days
  const currentAcwr = useMemo(() => {
    const mostRecentAcwrScore = getMostRecentAcwrScore(acwrScores);

    if (
      mostRecentAcwrScore?.date &&
      new Date(mostRecentAcwrScore?.date) >
        new Date(Date.now() - 2 * 24 * 60 * 60 * 1000)
    ) {
      return mostRecentAcwrScore.acwrScore;
    }
    return null;
  }, [acwrScores]);

  const loadHistory = useMemo(() => {
    // get the most recent 30 days
    const dates = getPreviousISODates(new Date(), 29);

    return dates
      .map(date => {
        let acwrDisplay = acwrScores.find(
          acwr => (acwr.date as string) === date,
        )?.acwrScore;
        if ((acwrDisplay ?? 0) > ACWR_MAX) {
          acwrDisplay = ACWR_MAX;
        }
        const acuteWorkload = acwrScores.find(
          acwr => (acwr.date as string) === date,
        )?.acuteWorkload;
        const chronicWorkload = acwrScores.find(
          acwr => (acwr.date as string) === date,
        )?.chronicWorkload;
        return {
          date,
          acwr: acwrDisplay ?? null,
          acuteWorkload: acuteWorkload ?? null,
          chronicWorkload: chronicWorkload ?? null,
        };
      })
      .reverse();
  }, [acwrScores]);

  return (
    <Box sx={{marginLeft: 2, marginRight: 2}}>
      <Typography sx={{color: ORANGE_COLOR}} variant="h5">
        {t('statistics.playerLoad')}
      </Typography>
      <Box sx={{display: 'flex', alignItems: 'center', mb: 2}}>
        <Grid container spacing={2}>
          <Grid
            item
            xs={12}
            sm={3}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}>
            <PlayerACWRGasGauge
              acwr={currentAcwr ?? 0}
              label={t('statistics.loadRatio')}
              labelSmall={`(${t('general.today')})`}
            />
          </Grid>
          <Grid
            item
            xs={12}
            sm={9}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}>
            {loadHistory && <LoadGraph loadHistory={loadHistory} />}
          </Grid>
        </Grid>
      </Box>
      <Alert severity="info" variant="outlined" sx={{mb: 2}}>
        {t('statistics.playerLoadInfo')}
      </Alert>
    </Box>
  );
};

export default PlayerLoadRow;
